import AnimatedPlayerSharing from '@/components/Animated/Backgrounds/AnimatedPlayerSharingBackground';
import {
  useGetArtworkReactions,
  usePlayer,
  useReactToArtwork,
  useReactions,
  useRoundArtwork,
  useSharingPlayer
} from '@/hooks/PlayerSessionControlHooks';
import { Reaction, playerGameReactions } from '@/utils/reactions';
import clsx from 'clsx';
import { AnimatePresence, motion } from 'motion/react';
import { useEffect, useState } from 'react';
import Canvas, { LineProps } from '../Controls/Canvas';

interface PlayerSpotlightProps {
  isSharing?: boolean;
}

export default function PlayerSpotlight({ isSharing = false }: PlayerSpotlightProps) {
  const player = usePlayer();
  const playerCards = useRoundArtwork();
  const sharingPlayer = useSharingPlayer();
  const reactions = useReactions();

  const reactToArtwork = useReactToArtwork();
  const getArtworkReactions = useGetArtworkReactions();

  const [showInitial, setShowInitial] = useState(true);
  const [drawing, setDrawing] = useState<LineProps[]>([]);
  const [selectedReactions, setSelectedReactions] = useState<Reaction[]>([]);

  useEffect(() => {
    if (!player || !sharingPlayer) return;

    const sharingPlayerCard = playerCards.find((card) => card?.playerId === sharingPlayer.id);
    if (!sharingPlayerCard) return;

    getArtworkReactions();
    try {
      const parsed = JSON.parse(sharingPlayerCard.drawing ?? '[]');
      setDrawing(Array.isArray(parsed) ? parsed : []);
    } catch {
      setDrawing([]);
    }
  }, [player, sharingPlayer, playerCards, getArtworkReactions]);

  useEffect(() => {
    const selectedReactionsTypes = reactions.map((reaction) => reaction.reactionType);

    setSelectedReactions(
      playerGameReactions.filter((reaction) => selectedReactionsTypes.includes(reaction.value))
    );
  }, [reactions]);

  const handleReactionClick = async (reaction: Reaction) => {
    if (selectedReactions.length > 0) {
      return;
    }

    try {
      setSelectedReactions([...selectedReactions, reaction]);
      await reactToArtwork(reaction);
    } catch (error) {
      console.error('Failed to create artwork reaction:', error);
    }
  };

  const renderReactionImage = (reaction: Reaction, index: number, isUserReaction = false) => (
    <motion.div
      initial={{ opacity: 0, scale: 0, y: 50 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0, y: -50 }}
      transition={{ duration: 0.1 }}
      key={index}
      className="flex flex-col items-center text-center py-4 px-12 z-10"
    >
      <img
        src={reaction.src}
        alt={reaction.label}
        className={clsx(
          'w-48 h-48 mb-12',
          !isUserReaction && selectedReactions.length > 0 && 'cursor-not-allowed grayscale',
          !isUserReaction && selectedReactions.length === 0 && 'cursor-pointer'
        )}
        onClick={() => !isUserReaction && handleReactionClick(reaction)}
      />
      {!isUserReaction && (
        <span className="rounded-xl px-4 text-xs font-bold">{reaction.label}</span>
      )}
    </motion.div>
  );

  return (
    <>
      <AnimatedPlayerSharing />

      <AnimatePresence mode="wait">
        {!showInitial ? (
          <div className="absolute inset-0 min-h-screen min-w-screen h-full w-full flex items-center justify-center">
            <div className="max-w-lg w-full mx-auto px-8">
              <motion.div
                key="spotlight"
                initial={{ opacity: 0, y: -200, rotate: 15 }}
                animate={{ opacity: 1, y: 0, rotate: 0 }}
                exit={{ opacity: 0, y: -200 }}
                transition={{ duration: 0.3, type: 'spring', stiffness: 100 }}
                className="flex flex-col items-center justify-center"
              >
                <h1 className="mt-24 text-[#1D1A34] font-extrabold text-xl">
                  {isSharing
                    ? "It's your turn to share!"
                    : `${sharingPlayer?.name ?? ''} is sharing!`}
                </h1>
                {!isSharing && (
                  <div className="flex flex-row items-center justify-center gap-[2px] w-full min-h-48 -mb-48 z-10">
                    {selectedReactions.map((reaction, index) =>
                      renderReactionImage(reaction, index, true)
                    )}
                  </div>
                )}
                <div className="w-[90%] h-[90%]">
                  <Canvas drawing={drawing ?? []} isViewerMode={true} />
                </div>
                {!isSharing && (
                  <div className="w-full">
                    <div className="min-h-112">
                      <AnimatePresence mode="sync">
                        {selectedReactions.length < playerGameReactions.length && (
                          <>
                            <h2 className="text-base font-black text-center text-black pb-12">
                              How do you connect with {sharingPlayer?.name}&apos;s story?
                            </h2>
                            <motion.div
                              className="reaction-list flex items-center justify-center w-full rounded-2xl p-8 bg-slate-100/50 "
                              initial={{ opacity: 0, scale: 0, y: 50 }}
                              animate={{ opacity: 1, scale: 1, y: 0 }}
                              exit={{ opacity: 0, scale: 0, y: -50 }}
                              transition={{ duration: 0.5 }}
                            >
                              {playerGameReactions
                                .filter((reaction) => !selectedReactions.includes(reaction))
                                .map((reaction, index) => renderReactionImage(reaction, index))}
                            </motion.div>
                          </>
                        )}
                      </AnimatePresence>
                    </div>
                  </div>
                )}
              </motion.div>
            </div>
          </div>
        ) : (
          <motion.div
            key="initial"
            initial={{ opacity: 0, y: -200 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -200 }}
            transition={{ duration: 0.3, type: 'spring', stiffness: 100 }}
            onAnimationComplete={() => setShowInitial(false)}
            className="absolute inset-0 min-h-screen min-w-screen h-full w-full flex items-center justify-center -mt-20"
          >
            <p className="font-extrabold text-center text-2xl text-[#1D1A34] mt-20">
              {!isSharing ? 'Time to see some artwork!' : `It's your turn to share!`}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
